import React, { useEffect } from 'react'

import { useForm } from 'react-hook-form'

import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'

import { useTwoFactorAuth } from '@dentalux/security'

import { yupResolver } from '@hookform/resolvers/yup'
import { LocalStorage } from 'src/@types/LocalStorage'
import { DialogNames } from 'src/components/dialogs/DialogRoot'
import { OTPFields } from 'src/components/inputs/OtpInput/OtpInput'
import { schema } from 'src/components/OTPForm/OTPForm.validation'
import { reset as authorizedReset, useAuthorizedDispatch } from 'src/components/providers/AuthorizedProvider'
import { useDialog } from 'src/components/providers/DialogProvider'
import { useSnackbar } from 'src/components/providers/SnackbarProvider'
import {
  reset as unauthorizedReset,
  useUnauthorizedContext,
  useUnauthorizedDispatch,
} from 'src/components/providers/UnauthorizedProvider'
import securityHelpers from 'src/config/security'
import { useBiometricLoginState } from 'src/hooks/useBiometricLoginState'
import MFAScreen from 'src/screens/MFA'
import withTranslation from 'src/staticProps/withTranslation'

const MFAPage = () => {
  const router = useRouter()

  const search = useSearchParams()

  const isOnboardingDismissed = localStorage.getItem(LocalStorage.OnboardingDismissed)
    ? JSON.parse(localStorage.getItem(LocalStorage.OnboardingDismissed))
    : ''
  const { shouldOfferBiometricLogin, biometricLoginReset } = useBiometricLoginState()

  useEffect(() => {
    router.prefetch('/')
    if (!isOnboardingDismissed) router.prefetch('/onboarding')
  }, [router, isOnboardingDismissed])

  const unauthorizedDispatch = useUnauthorizedDispatch()
  const authorizedDispatch = useAuthorizedDispatch()
  const { preferredLanguageCode, shouldSyncLanguage } = useUnauthorizedContext()

  const form = useForm<OTPFields>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  })

  const { openDialog } = useDialog()

  const { openSnackbar } = useSnackbar()

  const {
    handleTowFactorVerification: submit,
    isLoading,
    error,
  } = useTwoFactorAuth({
    onError: () => {
      form.resetField('code')
    },
    onSuccess: () => {
      const successPath = securityHelpers.getConfig({ search }).successPath

      unauthorizedDispatch(unauthorizedReset())
      authorizedDispatch(authorizedReset())
      router.push(successPath).then(async () => {
        if (await shouldOfferBiometricLogin()) {
          openDialog(DialogNames.BiometricLogin, { openSnackbar })
        }

        localStorage.removeItem(LocalStorage.RedirectUrl)
      })
    },
  })

  return (
    <MFAScreen
      deviceSavingPossible
      onSubmit={({ code, rememberDevice }) => {
        if (rememberDevice && code) {
          biometricLoginReset()
        }

        return submit({
          ...(shouldSyncLanguage && { preferredLanguageCode }),
          ...(rememberDevice && { rememberDevice }),
          code: code.join(''),
        })
      }}
      form={form}
      isLoading={isLoading}
      error={error}
    />
  )
}

export const getStaticProps = withTranslation

export default MFAPage
